import { forwardRef, useEffect, useId, useRef, useState, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import type { DsSize } from '@/ds/tone'
import { DsFieldFrame, DsFieldShell } from '@/ds/field'
import { DsIconGlyph } from '@/ds/icon'
import { DsIcons, type DsIcon } from '@/ds/icons'
import { DsSpinner } from '@/ds/spinner'

export type DsInputVariant = 'outline' | 'filled'

export interface DsInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange' | 'value' | 'defaultValue' | 'prefix' | 'onSubmit'> {
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  /** Wait this long after the last keystroke before calling onChange. */
  debounce?: number
  onSubmit?: (value: string) => void
  label?: string
  hintText?: string
  helper?: string
  error?: string | null
  /** Mark the frame invalid when the message is shown elsewhere (a shared field shell). */
  invalid?: boolean
  required?: boolean
  optionalLabel?: string
  prefixIcon?: DsIcon
  prefix?: ReactNode
  suffixIcon?: DsIcon
  suffix?: ReactNode
  obscureText?: boolean
  readOnly?: boolean
  disabled?: boolean
  loading?: boolean
  showClear?: boolean
  maxLength?: number
  showCounter?: boolean
  /** Above 1 renders a textarea with that many visible rows. */
  maxLines?: number
  minLines?: number
  size?: DsSize
  variant?: DsInputVariant
  semanticLabel?: string
  className?: string
}

/** Text field. Multiline = maxLines > 1; there is no separate textarea component. */
export const DsInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, DsInputProps>(function DsInput(
  { value, defaultValue, onChange, debounce, onSubmit, label, hintText, helper, error, invalid, required, optionalLabel, prefixIcon, prefix, suffixIcon, suffix, obscureText, readOnly, disabled, loading, showClear, maxLength, showCounter, maxLines = 1, minLines, size = 'md', variant = 'outline', semanticLabel, className, id: idProp, onFocus, onBlur, onKeyDown, ...rest },
  ref,
) {
  const generatedId = useId()
  const id = idProp ?? generatedId
  const [focused, setFocused] = useState(false)
  const [shown, setShown] = useState(false)
  const [inner, setInner] = useState(defaultValue ?? '')
  const isControlled = value !== undefined
  // A controlled field with a debounce holds what was typed until onChange fires (LP-839): without it
  // React puts the DOM back to the unchanged prop after every key and only the last letter survives.
  // The draft is dropped whenever the prop changes, so the parent stays the source of truth.
  const [draft, setDraft] = useState<string | null>(null)
  const [syncedValue, setSyncedValue] = useState(value)
  if (value !== syncedValue) {
    setSyncedValue(value)
    setDraft(null)
  }
  const current = isControlled ? draft ?? value : inner
  const timer = useRef<number | undefined>(undefined)
  const pending = useRef<string | null>(null)
  useEffect(() => () => window.clearTimeout(timer.current), [])

  const emit = (next: string, now = false) => {
    if (!isControlled) setInner(next)
    else if (debounce) setDraft(next)
    if (!onChange) return
    window.clearTimeout(timer.current)
    pending.current = null
    if (debounce && !now) {
      pending.current = next
      timer.current = window.setTimeout(() => {
        pending.current = null
        onChange(next)
      }, debounce)
    } else onChange(next)
  }
  /** Sends a debounced value that is still waiting, so Enter acts on what is in the field. */
  const flush = () => {
    if (pending.current === null) return
    const next = pending.current
    window.clearTimeout(timer.current)
    pending.current = null
    onChange?.(next)
  }

  const multiline = maxLines > 1
  const commonProps = {
    id,
    value: current,
    placeholder: hintText,
    readOnly,
    disabled: disabled || loading,
    maxLength,
    'aria-label': semanticLabel,
    'aria-invalid': error || invalid ? true : undefined,
    'aria-describedby': error || helper ? `${id}-helper` : undefined,
    className: 'min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed',
    onFocus: (e: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement>) => {
      setFocused(true)
      onFocus?.(e as React.FocusEvent<HTMLInputElement>)
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement>) => {
      setFocused(false)
      onBlur?.(e as React.FocusEvent<HTMLInputElement>)
    },
  }
  const counter = showCounter && maxLength ? `${current.length}/${maxLength}` : undefined

  return (
    <DsFieldShell label={label} required={required} optionalLabel={optionalLabel} helper={helper} error={error} counter={counter} disabled={disabled} htmlFor={id} className={className}>
      <DsFieldFrame focused={focused} hasError={!!error || !!invalid} filled={variant === 'filled'} size={size} multiline={multiline} disabled={disabled}>
        {prefixIcon && <DsIconGlyph icon={prefixIcon} size={18} muted className={multiline ? 'mt-0.5' : undefined} />}
        {prefix && <span className="text-muted-foreground">{prefix}</span>}
        {multiline ? (
          <textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            rows={minLines ?? Math.min(maxLines, 4)}
            onChange={(e) => emit(e.target.value)}
            onKeyDown={onKeyDown as unknown as React.KeyboardEventHandler<HTMLTextAreaElement>}
            {...commonProps}
            className={cn(commonProps.className, 'resize-y leading-[1.45]')}
          />
        ) : (
          <input
            ref={ref as React.Ref<HTMLInputElement>}
            type={obscureText && !shown ? 'password' : rest.type ?? 'text'}
            onChange={(e) => emit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                flush()
                onSubmit?.(current)
              }
              onKeyDown?.(e)
            }}
            {...rest}
            {...commonProps}
          />
        )}
        {loading && <DsSpinner size={16} />}
        {showClear && current.length > 0 && !disabled && !readOnly && (
          <button type="button" aria-label="Clear" onClick={() => emit('', true)} className="-mr-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground">
            <DsIconGlyph icon={DsIcons.closeCircle} size={16} />
          </button>
        )}
        {obscureText && (
          <button type="button" aria-label={shown ? 'Hide password' : 'Show password'} aria-pressed={shown} onClick={() => setShown((s) => !s)} className="-mr-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground">
            <DsIconGlyph icon={shown ? DsIcons.eyeSlash : DsIcons.eye} size={18} />
          </button>
        )}
        {suffixIcon && <DsIconGlyph icon={suffixIcon} size={18} muted />}
        {suffix && <span className="text-muted-foreground">{suffix}</span>}
      </DsFieldFrame>
    </DsFieldShell>
  )
})
